import { EstimatePriceInput } from "./EstimatePriceInput";
import { useLayoutEffect, useMemo, useRef, type TextareaHTMLAttributes } from "react";
import { organizeEquipment, type EquipmentItem } from "../lib/equipment-catalog";
import { groupInvoiceLines } from "../lib/invoice-line-groups";
import { QuietButton } from "./controls";

export function EstimateDescription(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const fit = () => {
      element.style.height = "auto";
      if (element.scrollHeight) element.style.height = `${element.scrollHeight + 2}px`;
    };
    fit();
    let width = element.clientWidth;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      if (element.clientWidth !== width) { width = element.clientWidth; fit(); }
    });
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [props.value]);
  return <textarea {...props} ref={ref} rows={props.rows ?? 2} style={{ ...props.style, overflow: "hidden", resize: "vertical" }} />;
}

type Line = { id?: string; priceBookItemId?: string | null; itemName?: string; description: string; quantity: number; unitPriceCents: number };
type Category = { id: string; name: string; kind: string };
export function EstimateOptionItems({ lines, items, categories, optionNumber, onChange, onRemove }: {
  lines: Line[];
  items: (EquipmentItem & { costCents?: number | null })[];
  categories: Category[];
  optionNumber: number;
  onChange: (index: number, change: Partial<Line>) => void;
  onRemove: (index: number) => void;
}) {
  const catalog = useMemo(() => {
    const equipmentIds = new Set(organizeEquipment(items, categories).map(entry => entry.item.id));
    // Imported equipment can share service categories. Reuse the exact catalog
    // identities already used by Add Equipment without changing saved assignments.
    const equipmentCategory = "__estimate_equipment_display__";
    return {
      byId: new Map(items.map(item => [item.id, item])),
      items: items.map(item => equipmentIds.has(item.id) ? { ...item, categoryId: equipmentCategory } : item),
      categories: [...categories, { id: equipmentCategory, name: "Equipment", kind: "equipment" }],
    };
  }, [items, categories]);
  const groups = groupInvoiceLines(lines.map((line, index) => ({ ...line, index })), catalog.items, catalog.categories);
  const label = (name: string) => name === "Labor / install" ? "Labor" : name === "Materials" ? "Chargeable materials" : name;
  if (lines.length === 0) return null;
  return <div className="mt-3 space-y-4" aria-label={`Option ${optionNumber} items`}>
    {groups.map(([name, rows]) => <section key={name} aria-label={label(name)} className="space-y-2">
      {rows.map(line => {
        const item = line.priceBookItemId ? catalog.byId.get(line.priceBookItemId) : undefined;
        const itemName = item?.name || line.itemName;
        const field = `Office option ${optionNumber} line ${line.index + 1}`;
        return <div key={line.id ?? line.index} className="grid items-start gap-2 rounded-lg border border-[var(--line)] p-3 sm:grid-cols-[minmax(0,1fr)_70px_90px_100px_auto]">
          {itemName && <div className="col-span-full -mx-3 -mt-3 mb-2 rounded-t-lg border-b border-[var(--line)] bg-[var(--wash)] px-3 py-3">
            <p className="mb-1 text-xs font-medium text-[var(--ink-soft)]">Item name</p>
            <p className="whitespace-pre-wrap break-words text-base font-semibold leading-6 text-[var(--ink)]">{itemName}</p>
          </div>}
          <div className="min-w-0">
            <label className="block text-xs text-[var(--ink-soft)]">Item description
              <EstimateDescription spellCheck autoCorrect="on" autoCapitalize="sentences" aria-label={`${field} item`} value={line.description} onChange={event => onChange(line.index, { description: event.target.value })} placeholder="Custom line" className="mt-1 block w-full rounded border border-[var(--line)] px-2 py-1.5 text-sm text-[var(--ink)]" />
            </label>
          </div>
          <label className="text-xs text-[var(--ink-soft)]">Quantity<input aria-label={`${field} quantity`} type="number" min="0.01" step="0.01" value={line.quantity} onChange={event => onChange(line.index, { quantity: Number(event.target.value) })} className="mt-1 block w-full rounded border border-[var(--line)] px-2 py-1.5 text-sm text-[var(--ink)]" /></label>
          <div className="text-xs text-[var(--ink-soft)]">Unit cost<span className="mt-1 block py-1.5 text-sm text-[var(--ink)]">{item?.costCents == null ? "Not set" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(item.costCents / 100)}</span></div>
          <label className="text-xs text-[var(--ink-soft)]">Unit price<EstimatePriceInput aria-label={`${field} price`} cents={line.unitPriceCents} onPriceChange={unitPriceCents => onChange(line.index, { unitPriceCents })} className="mt-1 block w-full rounded border border-[var(--line)] px-2 py-1.5 text-sm text-[var(--ink)]" /></label>
          <QuietButton type="button" onClick={() => onRemove(line.index)}>Remove</QuietButton>
        </div>;
      })}
    </section>)}
  </div>;
}
